import React, { useMemo } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import WidgetCard from './WidgetCard';
import { useDashboardStore } from '../../../store/useDashboardStore';
import { ValuationData } from '../../../types/valuation';
import { Link } from 'react-router-dom';

// Fallback benchmarks (same as PerformanceAnalysis.tsx)
interface Benchmark {
  label: string;
  key: string;
  avg: number;
  unit: string;
  format: string;
  inversed?: boolean;
}

const benchmarks: Benchmark[] = [
  { label: 'Annual Fuel Volume', key: 'annualGallons', avg: 1620000, unit: 'gal', format: 'number' },
  { label: 'Fuel Profit Margin', key: 'fuelMargin', avg: 0.28, unit: '$/gal', format: 'margin' },
  { label: 'C-Store Revenue', key: 'storeRevenue', avg: 68333, unit: '$/mo', format: 'currency' },
  { label: 'Operating Expenses', key: 'operatingExpenses', avg: 40000, unit: '$/mo', format: 'currency', inversed: true },
  { label: 'Property Size', key: 'propertySize', avg: 2800, unit: 'SF', format: 'number' },
];

function getMetricValue(data: ValuationData, key: string): number {
  return (data as any)[key] || 0;
}

function formatValue(value: number, format: string): string {
  if (value === 0) return '—';
  switch (format) {
    case 'currency':
      return value >= 1000 ? `$${Math.round(value / 1000)}K` : `$${Math.round(value)}`;
    case 'margin':
      return `$${value.toFixed(2)}`;
    case 'number':
    default:
      return value >= 1000000
        ? `${(value / 1000000).toFixed(1)}M`
        : value >= 1000
        ? `${Math.round(value / 1000).toLocaleString()}K`
        : Math.round(value).toLocaleString();
  }
}

function getPerformanceRatio(value: number, avg: number, inversed?: boolean): number {
  if (avg === 0 || value === 0) return 0;
  const ratio = value / avg;
  return inversed ? 1 / ratio : ratio;
}

export default function PropertyAnalyticsWidget() {
  const {
    valuations,
    selectedAnalyticsPropertyId,
    setAnalyticsProperty,
    isLoadingValuations,
    valuationsError,
  } = useDashboardStore();

  const selectedValuation = useMemo(
    () => valuations.find((v) => v.id === selectedAnalyticsPropertyId),
    [valuations, selectedAnalyticsPropertyId]
  );

  return (
    <WidgetCard
      title="Property Analytics"
      icon={<BarChart3 className="w-5 h-5" />}
      colSpan={3}
      isLoading={isLoadingValuations}
      error={valuationsError}
      action={
        selectedAnalyticsPropertyId ? (
          <Link
            to={`/dashboard/performance-analysis/${selectedAnalyticsPropertyId}`}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View Full Analysis
          </Link>
        ) : null
      }
    >
      {valuations.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Run a valuation to see how your property compares to market benchmarks.
        </p>
      ) : (
        <div className="space-y-4">
          {/* Property selector */}
          <div>
            <select
              value={selectedAnalyticsPropertyId || ''}
              onChange={(e) => setAnalyticsProperty(e.target.value || null)}
              className="w-full md:w-auto text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {valuations.map((v) => (
                <option key={v.id} value={v.id}>
                  {(v.data.location || 'Unnamed Property').substring(0, 50)}
                </option>
              ))}
            </select>
          </div>

          {/* Metrics vs benchmarks */}
          {selectedValuation && (
            <div className="space-y-3">
              {benchmarks.map((b) => {
                const value = getMetricValue(selectedValuation.data, b.key);
                const ratio = getPerformanceRatio(value, b.avg, b.inversed);
                const pct = Math.min(Math.max(ratio * 50, 5), 95); // Scale to 5-95% bar width

                let barColor = 'bg-yellow-400';
                let textColor = 'text-yellow-700';
                let TrendIcon = Minus;

                if (ratio > 1.1) {
                  barColor = 'bg-green-500';
                  textColor = 'text-green-700';
                  TrendIcon = TrendingUp;
                } else if (ratio < 0.9) {
                  barColor = 'bg-red-400';
                  textColor = 'text-red-700';
                  TrendIcon = TrendingDown;
                }

                return (
                  <div key={b.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">{b.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-900">
                          {formatValue(value, b.format)}
                        </span>
                        <span className="text-xs text-gray-400">
                          vs {formatValue(b.avg, b.format)} avg
                        </span>
                        {value > 0 && (
                          <TrendIcon className={`w-3.5 h-3.5 ${textColor}`} />
                        )}
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      {/* Industry average marker */}
                      <div className="relative w-full h-full">
                        <div
                          className={`absolute left-0 top-0 h-full rounded-full transition-all ${barColor}`}
                          style={{ width: `${pct}%` }}
                        />
                        {/* 50% marker = industry average */}
                        <div className="absolute left-1/2 top-0 w-0.5 h-full bg-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-gray-400 mt-2">
                Center line represents industry average. Green = above average, Red = below average.
              </p>
            </div>
          )}
        </div>
      )}
    </WidgetCard>
  );
}
